import React from 'react';
import Select from 'react-select';
import UserGroupPermissions, {
  editPermissionRefs,
} from '../../../core/components/UserGroupPermissions';
import { ReclamationType } from '../models';

const CLAIM_NOTIFICATION_TIMING_HOUR_OPTIONS = [8, 16, 24, 48, 72];

const NotificationTimingControl = ({
  reclamationType,
  sites,
  notificationTimingScope,
  notificationTimingHours,
  notificationTimingSiteIds,
  notificationTimingDirty,
  onTimingChange,
  onSave,
}) => {
  if (reclamationType !== ReclamationType.Claim) {
    return null;
  }

  const siteOptions = (sites || []).map((site) => ({
    value: site.id,
    label: site.externalId ? `${site.externalId} (${site.name})` : site.name || `Site ${site.id}`,
  }));

  const selectedSet = new Set(notificationTimingSiteIds || []);
  const selectedSiteOptions = siteOptions.filter((option) => selectedSet.has(String(option.value)));

  const requiresSiteSelection =
    notificationTimingScope === 'site' && selectedSiteOptions.length === 0;

  return (
    <UserGroupPermissions itemRef={editPermissionRefs.claim_notification_timing}>
      <div className="claim-notification-timing-config">
        <div className="claim-notification-timing-config__header">
          <div className="claim-notification-timing-config__title-block">
            <div className="claim-notification-timing-config__eyebrow">Notification Settings</div>
            <div className="claim-notification-timing-config__title">Claim notification timing</div>
            <div className="claim-notification-timing-config__summary">
              Control when managers are reminded that a claim email is waiting for approval and
              send.
            </div>
          </div>
          <div className="claim-notification-timing-config__status">
            <span className="claim-notification-timing-config__status-label">
              Effective setting
            </span>
            <span className="claim-notification-timing-config__status-value">
              {notificationTimingScope === 'global'
                ? `Using Global (${notificationTimingHours}h)`
                : `Override (${notificationTimingHours}h)`}
            </span>
          </div>
        </div>
        <div className="claim-notification-timing-config__controls">
          <label className="claim-notification-timing-config__field">
            <span className="claim-notification-timing-config__field-label">Scope</span>
            <select
              className="form-control claim-notification-timing-config__select"
              value={notificationTimingScope}
              onChange={(event) =>
                onTimingChange({
                  notificationTimingScope: event.target.value,
                })
              }
            >
              <option value="site">Per site</option>
              <option value="global">Global</option>
            </select>
          </label>
          {notificationTimingScope === 'site' && (
            <div className="claim-notification-timing-config__field claim-notification-timing-config__field--sites">
              <label
                htmlFor="claim-notification-site-select"
                className="claim-notification-timing-config__field-label"
              >
                Sites
              </label>
              <Select
                inputId="claim-notification-site-select"
                isMulti
                options={siteOptions}
                value={selectedSiteOptions}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 20 }),
                  menu: (base) => ({ ...base, zIndex: 20 }),
                }}
                onChange={(selectedOptions) =>
                  onTimingChange({
                    notificationTimingSiteIds: (selectedOptions || []).map((option) =>
                      String(option.value)
                    ),
                  })
                }
                className="custom-tx-select claim-notification-timing-config__site-select"
                classNamePrefix="custom-tx"
                placeholder="Search sites..."
              />
            </div>
          )}
          <label className="claim-notification-timing-config__field">
            <span className="claim-notification-timing-config__field-label">Notify after</span>
            <select
              className="form-control claim-notification-timing-config__select"
              value={notificationTimingHours}
              onChange={(event) =>
                onTimingChange({
                  notificationTimingHours: Number(event.target.value),
                })
              }
            >
              {CLAIM_NOTIFICATION_TIMING_HOUR_OPTIONS.map((hours) => (
                <option key={hours} value={hours}>
                  {hours} hours
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="button primary claim-notification-timing-config__save"
            disabled={!notificationTimingDirty || requiresSiteSelection}
            onClick={onSave}
          >
            Save
          </button>
        </div>
        {notificationTimingScope === 'site' && (
          <div className="claim-notification-timing-config__supporting-text">
            <div className="claim-notification-timing-config__field-help">
              Choose which sites should use this override.
            </div>
            {requiresSiteSelection && (
              <div className="claim-notification-timing-config__validation">
                Select at least one site for per-site timing.
              </div>
            )}
          </div>
        )}
      </div>
    </UserGroupPermissions>
  );
};

export default NotificationTimingControl;
